/**
 * When a scheduled workflow runs: which days (of the week or of the month), and
 * at what time.
 *
 * The clock is the tenant's, not the reader's browser — a workflow set to 7am
 * runs at 7am where the business is, which is why the zone is stated rather
 * than left to be assumed from the machine.
 */
import { Input } from '@servicecompressionllc/sc-design-system';
import type { BuilderSchedule } from '../../lib/types.js';
import { WeekdayPicker } from './WeekdayPicker.js';

/** What a new schedule starts as: every weekday morning. */
export const DEFAULT_SCHEDULE: BuilderSchedule = { days: [1, 2, 3, 4, 5], time: '07:00' };

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const pill = (active: boolean): string =>
  [
    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
    active
      ? 'border-primary bg-primary text-white'
      : 'border-outline-strong text-ink-muted hover:text-ink',
  ].join(' ');

const dayButton = (active: boolean): string =>
  [
    'h-7 w-7 rounded-full border text-xs font-medium tabular-nums transition-colors',
    active
      ? 'border-primary bg-primary text-white'
      : 'border-outline-strong text-ink-muted hover:text-ink',
  ].join(' ');

/** Days of the month as a calendar-shaped grid; like the weekday picker, never empty. */
const MonthDayPicker = ({
  days,
  onChange,
}: {
  days: number[];
  onChange: (days: number[]) => void;
}): React.ReactElement => (
  <div className="grid w-max grid-cols-7 gap-1" role="group" aria-label="Days of the month">
    {MONTH_DAYS.map((d) => {
      const on = days.includes(d);
      return (
        <button
          key={d}
          type="button"
          aria-pressed={on}
          aria-label={`Day ${String(d)}`}
          className={dayButton(on)}
          onClick={() => {
            const next = on ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
            if (next.length === 0) return;
            onChange(next);
          }}
        >
          {d}
        </button>
      );
    })}
  </div>
);

export const ScheduleEditor = ({
  value,
  onChange,
}: {
  value: BuilderSchedule;
  onChange: (next: BuilderSchedule) => void;
}): React.ReactElement => {
  const monthly = 'monthDays' in value;
  // Only chat makes interval schedules; editing one here starts from the default time.
  const time = 'time' in value ? value.time : DEFAULT_SCHEDULE.time;
  return (
    <div className="grid gap-3">
      <div className="grid gap-1">
        <span className="text-xs text-ink-muted">Repeats</span>
        <div className="flex gap-1" role="group" aria-label="Repeats">
          <button
            type="button"
            aria-pressed={!monthly}
            className={pill(!monthly)}
            onClick={() => monthly && onChange({ days: [1, 2, 3, 4, 5], time })}
          >
            Weekly
          </button>
          <button
            type="button"
            aria-pressed={monthly}
            className={pill(monthly)}
            onClick={() => !monthly && onChange({ monthDays: [1], time })}
          >
            Monthly
          </button>
        </div>
      </div>

      {monthly ? (
        <div className="grid gap-1">
          <span className="text-xs text-ink-muted">Days of the month</span>
          <MonthDayPicker
            days={value.monthDays}
            onChange={(monthDays) => onChange({ monthDays, time })}
          />
          <span className="text-xs text-ink-subtle">
            A day a month doesn&rsquo;t have, like the 31st in April, runs on its last day.
          </span>
        </div>
      ) : (
        <div className="grid gap-1">
          <span className="text-xs text-ink-muted">Days</span>
          <WeekdayPicker
            days={'days' in value ? value.days : []}
            onChange={(days) => onChange({ days, time })}
          />
        </div>
      )}

      <label className="grid gap-1 text-xs text-ink-muted">
        Time
        <Input
          type="time"
          aria-label="Time it runs"
          value={time}
          onChange={(e) =>
            onChange(
              'time' in value
                ? { ...value, time: e.target.value }
                : { ...DEFAULT_SCHEDULE, time: e.target.value },
            )
          }
          className="w-32"
        />
      </label>

      <p className="text-xs text-ink-subtle">
        Runs in your organization&rsquo;s timezone, not the one on this computer. It can start a few
        minutes late: the platform checks every five minutes rather than at the exact second.
      </p>
    </div>
  );
};
